import React, { useState } from 'react';
import { Alert, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, GridToolbar, esES } from '@mui/x-data-grid';
import { handleConfirmModal, handleError } from '../../helpers/utils';
import { DialogTemplate } from '../dialogs/DialogTemplate';
import { NewUserForm } from '../auth/NewUserForm';
import { deleteUser, getUserInfo } from '../../actions/user';
import { useDispatch } from 'react-redux';
import { PhotoDialog } from '../auth/PhotoDialog';

// Users table
export const UsersTable = ({ data = [], userType = null, refresh = null }) => {
	// Dispatch
	const dispatch = useDispatch();

	// Dialog
	const [open, setOpen] = useState(false);
	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	// Dialog
	const [showNewOpenPhoto, setShowNewOpenPhoto] = useState(false);
	const handleCloseNewPhoto = () => setShowNewOpenPhoto(false);
	const handleOpenNewPhoto = () => setShowNewOpenPhoto(true);

	// Current user id
	const [currentUser, setCurrentUser] = useState(null);

	// Page size state
	const [pageSize, setPageSize] = useState(20);

	// Generate rows by array
	const generateRows = (array) => {
		if (array.length === 0) return [];

		// Filter users by user type & not deleted condition
		const filteredUsers = array.filter(
			(item) => item?.user_type?.name === userType
		);

		return filteredUsers.map((item) => ({
			id: item?.id,
			col1: item?.id,
			col2: `${item?.first_name} ${item?.last_name}`,
			col3: item?.email,
			col4: item?.user_type.name,
			col5: item?.area?.name || 'No aplica',
			...item,
		}));
	};

	// Handle delete user with modal confirmation
	const handleDeleteUser = (userId) => {
		handleConfirmModal(
			'¡Precaución!',
			'¿Estás seguro de eliminar permanentemente al usuario del sistema?',
			'warning',
			'Sí, eliminar'
		)
			.then(({ isConfirmed }) => {
				if (isConfirmed) {
					dispatch(deleteUser(userId));
				}
			})
			.catch((error) => handleError(error));
	};

	// Define columns
	const columns = [
		{
			field: 'col1',
			headerName: 'ID',
			width: 50,
			renderCell: ({ row }) => (
				<Tooltip title={row.col1}>
					<span className='table-cell-trucate'>{row.col1}</span>
				</Tooltip>
			),
		},
		{
			field: 'col2',
			headerName: 'Nombre',
			width: 250,
			renderCell: ({ row }) => (
				<Tooltip title={row.col2}>
					<span className='table-cell-trucate'>{row.col2}</span>
				</Tooltip>
			),
		},
		{
			field: 'col3',
			headerName: 'Correo',
			width: 270,
			renderCell: ({ row }) => (
				<Tooltip title={row.col3}>
					<span className='table-cell-trucate'>{row.col3}</span>
				</Tooltip>
			),
		},
		{
			field: 'col4',
			headerName: 'Tipo de usuario',
			width: 150,
			renderCell: ({ row }) => (
				<Tooltip title={row.col4}>
					<span className='table-cell-trucate'>{row.col4}</span>
				</Tooltip>
			),
		},
		{
			field: 'col5',
			headerName: 'Área',
			width: 200,
			renderCell: ({ row }) => (
				<Tooltip title={row.col5}>
					<span className='table-cell-trucate'>{row.col5}</span>
				</Tooltip>
			),
		},
		{
			field: 'position',
			headerName: 'Puesto',
			width: 200,
			renderCell: ({ row }) => (
				<Tooltip title={row.position}>
					<span className='table-cell-trucate'>{row.position}</span>
				</Tooltip>
			),
		},
		{
			field: 'phone_number',
			headerName: 'Número telefónico',
			width: 200,
			renderCell: ({ row }) => (
				<Tooltip title={row.phone_number}>
					<span className='table-cell-trucate'>
						{row.phone_number}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'col6',
			headerName: 'Acciones',
			width: 150,
			// Add actions to each case
			renderCell: ({ row }) => (
				<>
					<Tooltip
						title={
							row.img_profile.length > 0
								? 'Ver foto'
								: 'Nueva foto'
						}
					>
						<span>
							<IconButton
								style={{ color: '#143f6c' }}
								aria-label='delete'
								color='primary'
								onClick={() => {
									getUserInfo(row.id).then(({ data }) => {
										setCurrentUser(data);
										handleOpenNewPhoto();
									});
								}}
							>
								{row.img_profile.length > 0 ? (
									<AssignmentIndIcon />
								) : (
									<AddAPhotoIcon />
								)}
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title='Editar usuario'>
						<IconButton
							style={{ color: '#143f6c' }}
							aria-label='edit'
							color='primary'
							onClick={() => {
								getUserInfo(row.id).then(({ data }) => {
									setCurrentUser(data);
									handleOpen();
								});
							}}
						>
							<EditIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title='Eliminar usuario'>
						<IconButton
							style={{ color: '#143f6c' }}
							aria-label='delete'
							color='primary'
							onClick={() => handleDeleteUser(row.id)}
						>
							<DeleteIcon />
						</IconButton>
					</Tooltip>
				</>
			),
		},
	];

	return (
		<>
			{/* User form */}
			<DialogTemplate
				config={{
					open: open,
					handleClose: handleClose,
					btnCancel: false,
					btnConfirm: false,
					title: 'Editar usuario',
					subtitle: '',
				}}
				content={
					<NewUserForm
						handleClose={handleClose}
						data={currentUser}
						refresh={refresh}
					/>
				}
			/>

			{/* Photo dialog */}
			<PhotoDialog
				justWatch={currentUser?.img_profile.length > 0}
				user={currentUser}
				open={showNewOpenPhoto}
				handleClose={handleCloseNewPhoto}
				refresh={refresh}
			/>

			{/* Datagrid table */}
			{data.length > 0 ? (
				<DataGrid
					density='compact'
					autoHeight
					rows={generateRows(data)}
					columns={columns}
					pageSize={pageSize}
					pageSizeOptions={[5, 10, 25]}
					onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
					components={{ Toolbar: GridToolbar }}
					localeText={
						esES.components.MuiDataGrid.defaultProps.localeText
					}
				/>
			) : (
				<Alert severity='info'>No hay ningún usuario</Alert>
			)}
		</>
	);
};
